import { formatValue, formatInputValue, formatLabel, getInputType } from '../lib/formatters.js';

/*
 * Description: Data Display Web Component
 * Purpose: Example component that demonstrates DataStore integration and reactive updates
 * Author: Nuvemx
 * Created: 2026
 * 
 * Features:
 * - Displays data from the DataStore
 * - Automatically updates when data changes
 * - Allows editing data through input fields
 * - Shows real-time synchronization with server
 * - Demonstrates component binding to data paths
 * 
 * Usage:
 * <data-display path="user.profile" title="User Profile"></data-display>
 * <data-display path="settings" title="Application Settings"></data-display>
 * 
 * Attributes:
 * - path: String - The data path to display (e.g., 'user.profile')
 * - title: String - Title to display for this data section
 * - editable: Boolean - Whether the data can be edited (default: true)
 * - format: String - Display format: 'json', 'table', 'form' (default: 'form')
 * 
 * Methods:
 * - refresh(): Manually refresh the display
 * - save(): Save current data to server
 * - reset(): Reset to original values
 * 
 * Events:
 * - 'data-updated': Fired when component data is updated
 * - 'data-saved': Fired when data is saved to server
 * 
 * TODO:
 * - Add data validation
 * - Support for nested object editing
 * - Add undo/redo functionality
 */

class data_display extends HTMLElement {
    constructor() {
        super();
        this.boundPaths = new Set();
        this.originalData = {};
        this.isEditing = false;
    }
    
    connectedCallback() {
        if (this.dataset.rendered) {
            return;
        }
        const path = this.getAttribute('path') || 'demo';
        const title = this.getAttribute('title') || 'Data Display';
        const editable = this.getAttribute('editable') !== 'false';
        const format = this.getAttribute('format') || 'form';
        
        this.render(path, title, editable, format);
        this.addEventListener('click', this._handleClick);
        this.addEventListener('change', this._handleChange);
        this.bindToDataStore(path);
        
        // Initialize with some demo data if path doesn't exist
        if (!window.dataStore.get(path)) {
            this.initializeDemoData(path);
        }
        this.dataset.rendered = "true";
    }
    
    disconnectedCallback() {
        this.removeEventListener('click', this._handleClick);
        this.removeEventListener('change', this._handleChange);
        // Cleanup is handled automatically by DataStore
        //console.log('DataDisplay: Component disconnected');
    }
    
    _handleClick(event) {
        if (event.target.closest('.btn-outline-primary')) {
            this.toggleEdit();
        } else if (event.target.closest('.btn-outline-success')) {
            this.save();
        } else if (event.target.closest('.btn-outline-info')) {
            this.refresh();
        }
    }
    
    _handleChange(event) {
        const input = event.target.closest('input[data-field]');
        if (!input) return;
        this.updateField(input.dataset.field, input.value, input.dataset.type);
    }
    
    render(path, title, editable, format) {
        this.innerHTML = `
            <div class="card mb-3">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="mb-0">${title}</h5>
                    <div class="btn-group btn-group-sm">
                        ${editable ? `
                            <button class="btn btn-outline-primary">
                                <i class="bx bx-edit"></i> Edit
                            </button>
                            <button class="btn btn-outline-success">
                                <i class="bx bx-save"></i> Save
                            </button>
                        ` : ''}
                        <button class="btn btn-outline-info">
                            <i class="bx bx-refresh"></i> Refresh
                        </button>
                    </div>
                </div>
                <div class="card-body">
                    <div class="data-path-info mb-2">
                        <small class="text-muted">Path: <code>${path}</code></small>
                    </div>
                    <div class="data-content">
                        <!-- Data will be rendered here -->
                    </div>
                    <div class="data-status mt-2">
                        <small class="text-muted">Last updated: <span class="last-updated">Never</span></small>
                    </div>
                </div>
            </div>
        `;
    }
    
    bindToDataStore(path) {
        // Bind to the main path
        window.dataStore.bindComponent(this, path, (newValue, oldValue) => {
            this.updateDisplay(newValue);
            this.updateTimestamp();
            
            // Dispatch custom event
            this.dispatchEvent(new CustomEvent('data-updated', {
                detail: { path, newValue, oldValue }
            }));
        });
        
        this.boundPaths.add(path);
        //console.log(`DataDisplay: Bound to path ${path}`);
    }
    
    updateDisplay(data) {
        const contentDiv = this.querySelector('.data-content');
        const format = this.getAttribute('format') || 'form';
        
        if (!data) {
            contentDiv.innerHTML = '<p class="text-muted">No data available</p>';
            return;
        }
        
        switch (format) {
            case 'json':
                this.renderAsJSON(contentDiv, data);
                break;
            case 'table':
                this.renderAsTable(contentDiv, data);
                break;
            case 'form':
            default:
                this.renderAsForm(contentDiv, data);
                break;
        }
    }
    
    renderAsJSON(container, data) {
        container.innerHTML = `
            <pre class="bg-light p-3 rounded"><code>${JSON.stringify(data, null, 2)}</code></pre>
        `;
    }
    
    renderAsTable(container, data) {
        if (typeof data !== 'object' || Array.isArray(data)) {
            container.innerHTML = '<p class="text-warning">Table format requires an object</p>';
            return;
        }
        
        const rows = Object.entries(data).map(([key, value]) => `
            <tr>
                <td><strong>${key}</strong></td>
                <td>${formatValue(value)}</td>
            </tr>
        `).join('');
        
        container.innerHTML = `
            <table class="table table-sm">
                <thead>
                    <tr>
                        <th>Property</th>
                        <th>Value</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows}
                </tbody>
            </table>
        `;
    }
    
    renderAsForm(container, data) {
        if (typeof data !== 'object' || Array.isArray(data)) {
            container.innerHTML = `<p class="text-info">Value: ${formatValue(data)}</p>`;
            return;
        }
        
        const editable = this.getAttribute('editable') !== 'false';
        const path = this.getAttribute('path');
        
        const fields = Object.entries(data).map(([key, value]) => {
            const fieldPath = `${path}.${key}`;
            const inputType = getInputType(value);
            const inputId = `input-${fieldPath.replace(/\./g, '-')}`;
            
            return `
                <div class="mb-3">
                    <label for="${inputId}" class="form-label">${formatLabel(key)}</label>
                    ${editable ? `
                        <input 
                            type="${inputType}" 
                            class="form-control" 
                            id="${inputId}"
                            value="${formatInputValue(value, inputType)}"
                            data-field="${key}"
                            data-type="${inputType}"
                        >
                    ` : `
                        <div class="form-control-plaintext">${formatValue(value)}</div>
                    `}
                </div>
            `;
        }).join('');
        
        container.innerHTML = fields || '<p class="text-muted">No properties to display</p>';
    }
    
    updateField(key, value, inputType) {
        const path = this.getAttribute('path');
        const fieldPath = `${path}.${key}`;
        
        // Convert value based on input type
        let convertedValue = value;
        if (inputType === 'number') {
            convertedValue = parseFloat(value) || 0;
        } else if (inputType === 'checkbox') {
            convertedValue = value === 'true' || value === true;
        }
        
        // Update the data store
        window.dataStore.set(fieldPath, convertedValue);
        
        //console.log(`DataDisplay: Updated ${fieldPath} = ${convertedValue}`);
    }
    
    initializeDemoData(path) {
        const demoData = {
            name: 'John Doe',
            email: 'john.doe@example.com',
            age: 30,
            active: true,
            lastLogin: new Date().toISOString(),
            preferences: {
                theme: 'dark',
                notifications: true,
                language: 'en'
            }
        };
        
        window.dataStore.set(path, demoData);
        //console.log(`DataDisplay: Initialized demo data for ${path}`);
    }
    
    // Public methods
    
    refresh() {
        const path = this.getAttribute('path');
        const currentData = window.dataStore.get(path);
        this.updateDisplay(currentData);
        this.updateTimestamp();
        //console.log('DataDisplay: Refreshed');
    }
    
    async save() {
        try {
            await window.dataStore.saveToServer();
            this.showStatus('Data saved successfully', 'success');
            
            this.dispatchEvent(new CustomEvent('data-saved', {
                detail: { path: this.getAttribute('path') }
            }));
        } catch (error) {
            this.showStatus('Failed to save data', 'error');
            console.error('DataDisplay: Save failed', error);
        }
    }
    
    toggleEdit() {
        this.isEditing = !this.isEditing;
        const editBtn = this.querySelector('.btn-outline-primary');
        if (editBtn) {
            editBtn.innerHTML = this.isEditing ? 
                '<i class="bx bx-x"></i> Cancel' : 
                '<i class="bx bx-edit"></i> Edit';
        }
        
        // Re-render to show/hide edit controls
        const path = this.getAttribute('path');
        const currentData = window.dataStore.get(path);
        this.updateDisplay(currentData);
    }
    
    // Helper methods
    
    updateTimestamp() {
        const timestampEl = this.querySelector('.last-updated');
        if (timestampEl) {
            timestampEl.textContent = new Date().toLocaleTimeString();
        }
    }
    
    showStatus(message, type = 'info') {
        const statusDiv = this.querySelector('.data-status');
        const alertClass = type === 'success' ? 'alert-success' : 
                          type === 'error' ? 'alert-danger' : 'alert-info';
        
        const alert = document.createElement('div');
        alert.className = `alert ${alertClass} alert-dismissible fade show mt-2`;
        alert.innerHTML = `
            ${message}
            <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        `;
        
        statusDiv.appendChild(alert);
        
        // Auto-remove after 3 seconds
        setTimeout(() => {
            if (alert.parentNode) {
                alert.remove();
            }
        }, 3000);
    }
}

export { data_display };
